import React, { useMemo } from "react";
import { formatCiroMoney } from "@/utils/ciroHelpers";

export const TR_MONTHS = ["Oca", "Şub", "Mar", "Nis", "May", "Haz", "Tem", "Ağu", "Eyl", "Eki", "Kas", "Ara"];

export const formatTrendAxisLabel = (value, period) => {
  if (!value) return "";
  if (period === "yearly") return String(value);
  if (period === "monthly" && /^\d{4}-\d{2}$/.test(value)) {
    const [y, m] = value.split("-");
    return `${TR_MONTHS[Number(m) - 1] || m} ${y}`;
  }
  if (period === "daily" && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const [, m, d] = value.split("-");
    return `${Number(d)} ${TR_MONTHS[Number(m) - 1] || m}`;
  }
  return String(value);
};

export const formatMn = (value) => {
  const num = Number(value) || 0;
  const mn = num / 1_000_000;
  if (mn === 0) return "0";
  if (mn >= 10) return `${Math.round(mn)} Mn`;
  return `${mn.toLocaleString("tr-TR", { maximumFractionDigits: 1 })} Mn`;
};

export const formatCompactMoney = (value) => {
  const num = Number(value) || 0;
  if (num >= 1_000_000) return `${(num / 1_000_000).toLocaleString("tr-TR", { maximumFractionDigits: 1 })}M`;
  if (num >= 1_000) return `${(num / 1_000).toLocaleString("tr-TR", { maximumFractionDigits: 0 })}K`;
  return num.toLocaleString("tr-TR", { maximumFractionDigits: 0 });
};

const buildSmoothPath = (points) => {
  if (points.length === 0) return "";
  if (points.length === 1) return `M ${points[0].x} ${points[0].y}`;
  let d = `M ${points[0].x} ${points[0].y}`;
  for (let i = 0; i < points.length - 1; i += 1) {
    const p0 = points[i - 1] || points[i];
    const p1 = points[i];
    const p2 = points[i + 1];
    const p3 = points[i + 2] || p2;
    const cp1x = p1.x + (p2.x - p0.x) / 6;
    const cp1y = p1.y + (p2.y - p0.y) / 6;
    const cp2x = p2.x - (p3.x - p1.x) / 6;
    const cp2y = p2.y - (p3.y - p1.y) / 6;
    d += ` C ${cp1x} ${cp1y}, ${cp2x} ${cp2y}, ${p2.x} ${p2.y}`;
  }
  return d;
};

const getNiceMax = (maxVal) => {
  const mn = (Number(maxVal) || 0) / 1_000_000;
  if (mn <= 0) return 7.5;
  if (mn <= 7.5) return 7.5;
  if (mn <= 15) return 15;
  if (mn <= 22.5) return 22.5;
  if (mn <= 30) return 30;
  return Math.ceil(mn / 15) * 15;
};

export const TrendLineChart = ({ trend, period, isDark, valueKey = "total_ciro", color }) => {
  const chart = useMemo(() => {
    const values = (trend || []).map((t) => Number(t[valueKey]) || 0);
    if (!values.length) return null;

    const width = 760;
    const height = 220;
    const pad = { top: 12, right: 16, bottom: 32, left: 52 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;
    const maxVal = Math.max(...values, 1);
    const yMax = getNiceMax(maxVal) * 1_000_000;
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => r * yMax);

    const points = values.map((val, idx) => {
      const x = pad.left + (values.length === 1 ? innerW / 2 : (idx / (values.length - 1)) * innerW);
      const y = pad.top + innerH - (val / yMax) * innerH;
      return { x, y, val, label: trend[idx]?.period_label };
    });

    const labelCount = Math.min(7, points.length);
    const labelIndexes = Array.from({ length: labelCount }, (_, i) => {
      if (labelCount === 1) return 0;
      return Math.round((i / (labelCount - 1)) * (points.length - 1));
    });

    return { width, height, pad, innerW, innerH, yMax, yTicks, points, path: buildSmoothPath(points), labelIndexes };
  }, [trend, valueKey]);

  if (!chart) {
    return <p className={`text-xs py-8 text-center ${isDark ? "text-slate-400" : "text-gray-500"}`}>Trend verisi yok</p>;
  }

  const gridColor = isDark ? "#334155" : "#e5e7eb";
  const textColor = isDark ? "#94a3b8" : "#9ca3af";
  const lineColor = color || (isDark ? "#38bdf8" : "#2563eb");

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="w-full min-w-[520px] h-[220px]">
        {chart.yTicks.map((tick) => {
          const y = chart.pad.top + chart.innerH - (tick / chart.yMax) * chart.innerH;
          return (
            <g key={tick}>
              <line x1={chart.pad.left} y1={y} x2={chart.width - chart.pad.right} y2={y} stroke={gridColor} strokeDasharray="4 4" />
              <text x={chart.pad.left - 8} y={y + 4} textAnchor="end" fill={textColor} fontSize="10">{formatMn(tick)}</text>
            </g>
          );
        })}
        <path d={chart.path} fill="none" stroke={lineColor} strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" />
        {chart.points.map((p, idx) => (
          <circle key={`${p.label}-${idx}`} cx={p.x} cy={p.y} r="3" fill={lineColor} opacity={chart.points.length <= 24 ? 1 : 0}>
            <title>{`${formatTrendAxisLabel(p.label, period)}: ${formatCiroMoney(p.val)}`}</title>
          </circle>
        ))}
        {chart.labelIndexes.map((idx) => {
          const p = chart.points[idx];
          return (
            <text key={`lbl-${idx}`} x={p.x} y={chart.height - 8} textAnchor="middle" fill={textColor} fontSize="10">
              {formatTrendAxisLabel(p.label, period)}
            </text>
          );
        })}
      </svg>
    </div>
  );
};

export const StackedTrendChart = ({ trend, period, isDark }) => {
  const chart = useMemo(() => {
    if (!trend?.length) return null;
    const width = 760;
    const height = 240;
    const pad = { top: 12, right: 16, bottom: 32, left: 52 };
    const innerW = width - pad.left - pad.right;
    const innerH = height - pad.top - pad.bottom;

    const rows = trend.map((t) => ({
      label: t.period_label,
      hospital: Number(t.hospital_payments) || 0,
      doctor: Number(t.doctor_payments) || 0,
      total: Number(t.total_ciro) || 0,
    }));
    const maxVal = Math.max(...rows.map((r) => r.total), 1);
    const yMax = getNiceMax(maxVal) * 1_000_000;
    const yTicks = [0, 0.25, 0.5, 0.75, 1].map((r) => r * yMax);
    const barW = Math.min(48, Math.max(12, innerW / rows.length - 6));

    const bars = rows.map((row, idx) => {
      const x = pad.left + (rows.length === 1 ? innerW / 2 : (idx / (rows.length - 1)) * innerW) - barW / 2;
      const hH = (row.hospital / yMax) * innerH;
      const hD = (row.doctor / yMax) * innerH;
      const yH = pad.top + innerH - hH - hD;
      const yD = pad.top + innerH - hD;
      return { ...row, x, barW, hH, hD, yH, yD };
    });

    const labelCount = Math.min(7, bars.length);
    const labelIndexes = Array.from({ length: labelCount }, (_, i) => {
      if (labelCount === 1) return 0;
      return Math.round((i / (labelCount - 1)) * (bars.length - 1));
    });

    return { width, height, pad, innerH, yMax, yTicks, bars, labelIndexes };
  }, [trend]);

  if (!chart) {
    return <p className={`text-xs py-8 text-center ${isDark ? "text-slate-400" : "text-gray-500"}`}>Trend verisi yok</p>;
  }

  const gridColor = isDark ? "#334155" : "#e5e7eb";
  const textColor = isDark ? "#94a3b8" : "#9ca3af";
  const hospitalColor = isDark ? "#0ea5e9" : "#0284c7";
  const doctorColor = isDark ? "#a78bfa" : "#7c3aed";

  return (
    <div className="w-full overflow-x-auto">
      <svg viewBox={`0 0 ${chart.width} ${chart.height}`} className="w-full min-w-[520px] h-[240px]">
        {chart.yTicks.map((tick) => {
          const y = chart.pad.top + chart.innerH - (tick / chart.yMax) * chart.innerH;
          return (
            <g key={tick}>
              <line x1={chart.pad.left} y1={y} x2={chart.width - chart.pad.right} y2={y} stroke={gridColor} strokeDasharray="4 4" />
              <text x={chart.pad.left - 8} y={y + 4} textAnchor="end" fill={textColor} fontSize="10">{formatMn(tick)}</text>
            </g>
          );
        })}
        {chart.bars.map((bar, idx) => (
          <g key={`${bar.label}-${idx}`}>
            <rect x={bar.x} y={bar.yH} width={bar.barW} height={bar.hH} rx="3" fill={hospitalColor} opacity="0.92">
              <title>{`${formatTrendAxisLabel(bar.label, period)} · Hastane: ${formatCiroMoney(bar.hospital)}`}</title>
            </rect>
            <rect x={bar.x} y={bar.yD} width={bar.barW} height={bar.hD} rx="3" fill={doctorColor} opacity="0.92">
              <title>{`${formatTrendAxisLabel(bar.label, period)} · Klinik: ${formatCiroMoney(bar.doctor)}`}</title>
            </rect>
          </g>
        ))}
        {chart.labelIndexes.map((idx) => {
          const bar = chart.bars[idx];
          return (
            <text key={`lbl-${idx}`} x={bar.x + bar.barW / 2} y={chart.height - 8} textAnchor="middle" fill={textColor} fontSize="10">
              {formatTrendAxisLabel(bar.label, period)}
            </text>
          );
        })}
      </svg>
      <div className={`flex flex-wrap gap-4 mt-2 text-[11px] ${isDark ? "text-slate-400" : "text-gray-500"}`}>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-sky-500" /> Hastane payı</span>
        <span className="inline-flex items-center gap-1.5"><span className="w-2.5 h-2.5 rounded-sm bg-violet-500" /> Klinik/doktor payı</span>
      </div>
    </div>
  );
};

export const BarList = ({ items, isDark, valueKey = "total", labelKey = "label", subtitleKey, tone = "emerald" }) => {
  const max = useMemo(() => Math.max(...items.map((i) => Number(i[valueKey]) || 0), 1), [items, valueKey]);
  const gradients = {
    emerald: "from-emerald-500 to-teal-500",
    sky: "from-sky-500 to-blue-500",
    violet: "from-violet-500 to-purple-500",
    amber: "from-amber-500 to-orange-500",
    rose: "from-rose-500 to-pink-500",
  };
  const textTones = {
    emerald: isDark ? "text-emerald-300" : "text-emerald-700",
    sky: isDark ? "text-sky-300" : "text-sky-700",
    violet: isDark ? "text-violet-300" : "text-violet-700",
    amber: isDark ? "text-amber-300" : "text-amber-700",
    rose: isDark ? "text-rose-300" : "text-rose-700",
  };

  if (!items.length) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Veri yok</p>;
  }

  return (
    <div className="space-y-2.5">
      {items.map((item) => {
        const val = Number(item[valueKey]) || 0;
        const pct = Math.max(4, (val / max) * 100);
        return (
          <div key={`${item[labelKey]}-${val}`}>
            <div className="flex items-center justify-between gap-2 text-xs mb-1">
              <span className={`truncate ${isDark ? "text-slate-200" : "text-gray-800"}`} title={item[labelKey]}>
                {item[labelKey]}
              </span>
              <span className={`shrink-0 tabular-nums font-medium ${textTones[tone] || textTones.emerald}`}>
                {formatCiroMoney(val)}
                {subtitleKey && item[subtitleKey] != null ? (
                  <span className={`ml-1 opacity-70 ${isDark ? "text-slate-400" : "text-gray-500"}`}>
                    · {item[subtitleKey]}
                  </span>
                ) : null}
              </span>
            </div>
            <div className={`h-2 rounded-full overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <div className={`h-full rounded-full bg-gradient-to-r ${gradients[tone] || gradients.emerald}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const PaymentSplitChart = ({ items, isDark }) => {
  const total = useMemo(() => items.reduce((sum, item) => sum + (Number(item.total) || 0), 0), [items]);
  if (!items.length || total <= 0) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Veri yok</p>;
  }

  const colors = [
    { bar: "from-sky-500 to-blue-500", text: isDark ? "text-sky-300" : "text-sky-700" },
    { bar: "from-violet-500 to-purple-500", text: isDark ? "text-violet-300" : "text-violet-700" },
  ];

  return (
    <div className="space-y-4">
      <div className={`h-3 rounded-full overflow-hidden flex ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
        {items.map((item, idx) => {
          const pct = ((Number(item.total) || 0) / total) * 100;
          if (pct <= 0) return null;
          return (
            <div key={item.label} className={`h-full bg-gradient-to-r ${colors[idx]?.bar || colors[0].bar}`} style={{ width: `${pct}%` }} title={`${item.label}: ${formatCiroMoney(item.total)}`} />
          );
        })}
      </div>
      {items.map((item, idx) => {
        const val = Number(item.total) || 0;
        const pct = total > 0 ? ((val / total) * 100).toFixed(1) : 0;
        return (
          <div key={item.label}>
            <div className="flex items-center justify-between gap-2 text-xs mb-1">
              <span className={isDark ? "text-slate-200" : "text-gray-800"}>{item.label}</span>
              <span className={`font-semibold tabular-nums ${colors[idx]?.text || colors[0].text}`}>{formatCiroMoney(val)} · %{pct}</span>
            </div>
          </div>
        );
      })}
    </div>
  );
};

export const WeekdayChart = ({ items, isDark }) => {
  const chart = useMemo(() => {
    const order = ["Pazartesi", "Salı", "Çarşamba", "Perşembe", "Cuma", "Cumartesi", "Pazar"];
    const map = Object.fromEntries((items || []).map((i) => [i.label, i]));
    const rows = order.map((label) => ({
      label,
      total: Number(map[label]?.total) || 0,
      count: Number(map[label]?.count) || 0,
    }));
    const max = Math.max(...rows.map((r) => r.total), 1);
    return { rows, max };
  }, [items]);

  if (!items?.length) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Veri yok</p>;
  }

  return (
    <div className="space-y-2">
      {chart.rows.map((row) => {
        const pct = Math.max(6, (row.total / chart.max) * 100);
        return (
          <div key={row.label} className="flex items-center gap-2 text-xs">
            <span className={`w-16 shrink-0 ${isDark ? "text-slate-400" : "text-gray-500"}`}>{row.label.slice(0, 3)}</span>
            <div className={`flex-1 h-7 rounded-lg overflow-hidden ${isDark ? "bg-slate-800" : "bg-gray-100"}`}>
              <div
                className="h-full rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 flex items-center px-2 min-w-[2rem]"
                style={{ width: `${pct}%` }}
                title={`${row.label}: ${formatCiroMoney(row.total)} · ${row.count} işlem`}
              >
                {pct > 22 ? <span className="text-[10px] text-white font-medium truncate">{formatCompactMoney(row.total)}</span> : null}
              </div>
            </div>
            {pct <= 22 ? (
              <span className={`w-14 text-right tabular-nums ${isDark ? "text-slate-300" : "text-gray-700"}`}>{formatCompactMoney(row.total)}</span>
            ) : null}
          </div>
        );
      })}
    </div>
  );
};

export const DonutChart = ({ items, isDark, size = 140 }) => {
  const total = useMemo(() => items.reduce((s, i) => s + (Number(i.total) || 0), 0), [items]);
  const segments = useMemo(() => {
    if (!total) return [];
    const palette = ["#0ea5e9", "#8b5cf6", "#10b981", "#f59e0b", "#f43f5e", "#6366f1", "#14b8a6", "#ec4899"];
    let offset = 0;
    return items.map((item, idx) => {
      const val = Number(item.total) || 0;
      const pct = (val / total) * 100;
      const seg = { ...item, pct, offset, color: palette[idx % palette.length] };
      offset += pct;
      return seg;
    });
  }, [items, total]);

  if (!total) {
    return <p className={`text-xs ${isDark ? "text-slate-400" : "text-gray-500"}`}>Veri yok</p>;
  }

  const r = 42;
  const c = 2 * Math.PI * r;

  return (
    <div className="flex flex-col sm:flex-row items-center gap-4">
      <svg width={size} height={size} viewBox="0 0 100 100" className="shrink-0">
        <circle cx="50" cy="50" r={r} fill="none" stroke={isDark ? "#1e293b" : "#f3f4f6"} strokeWidth="14" />
        {segments.map((seg) => (
          <circle
            key={seg.label}
            cx="50"
            cy="50"
            r={r}
            fill="none"
            stroke={seg.color}
            strokeWidth="14"
            strokeDasharray={`${(seg.pct / 100) * c} ${c}`}
            strokeDashoffset={`${-((seg.offset / 100) * c)}`}
            transform="rotate(-90 50 50)"
            strokeLinecap="butt"
          >
            <title>{`${seg.label}: ${formatCiroMoney(seg.total)} (%${seg.pct.toFixed(1)})`}</title>
          </circle>
        ))}
        <text x="50" y="48" textAnchor="middle" fill={isDark ? "#e2e8f0" : "#111827"} fontSize="9" fontWeight="700">
          {formatCompactMoney(total)}
        </text>
        <text x="50" y="58" textAnchor="middle" fill={isDark ? "#94a3b8" : "#6b7280"} fontSize="6">toplam</text>
      </svg>
      <div className="flex-1 space-y-1.5 min-w-0">
        {segments.slice(0, 6).map((seg) => (
          <div key={seg.label} className="flex items-center gap-2 text-xs min-w-0">
            <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: seg.color }} />
            <span className={`truncate flex-1 ${isDark ? "text-slate-300" : "text-gray-700"}`} title={seg.label}>{seg.label}</span>
            <span className={`tabular-nums shrink-0 ${isDark ? "text-slate-400" : "text-gray-500"}`}>%{seg.pct.toFixed(0)}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

export const PeriodComparisonCard = ({ comparison, isDark, textClass, labelClass }) => {
  const current = comparison?.current || {};
  const previous = comparison?.previous || {};
  const deltaPct = Number(comparison?.delta_pct) || 0;
  const deltaAmount = Number(comparison?.delta_amount) || 0;
  const isUp = deltaPct >= 0;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-2 gap-3">
        <div className={`rounded-xl border p-3 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-gray-50"}`}>
          <p className={`text-[10px] uppercase tracking-wide ${labelClass}`}>Bu dönem</p>
          <p className={`text-lg font-bold tabular-nums ${textClass}`}>{formatCiroMoney(current.total_ciro)}</p>
          <p className={`text-[11px] ${labelClass}`}>{Number(current.total_operations || 0).toLocaleString("tr-TR")} işlem</p>
        </div>
        <div className={`rounded-xl border p-3 ${isDark ? "border-slate-700 bg-slate-800/40" : "border-gray-200 bg-gray-50"}`}>
          <p className={`text-[10px] uppercase tracking-wide ${labelClass}`}>Önceki dönem</p>
          <p className={`text-lg font-bold tabular-nums ${textClass}`}>{formatCiroMoney(previous.total_ciro)}</p>
          <p className={`text-[11px] ${labelClass}`}>{Number(previous.total_operations || 0).toLocaleString("tr-TR")} işlem</p>
        </div>
      </div>
      <div className={`rounded-xl border px-3 py-2.5 flex items-center justify-between ${isDark ? "border-slate-700" : "border-gray-200"}`}>
        <span className={`text-xs ${labelClass}`}>Ciro değişimi</span>
        <div className="text-right">
          <p className={`text-sm font-bold tabular-nums ${isUp ? (isDark ? "text-emerald-300" : "text-emerald-700") : (isDark ? "text-rose-300" : "text-rose-700")}`}>
            {isUp ? "+" : ""}{deltaPct.toLocaleString("tr-TR", { maximumFractionDigits: 1 })}%
          </p>
          <p className={`text-[11px] tabular-nums ${labelClass}`}>{isUp ? "+" : ""}{formatCiroMoney(deltaAmount)}</p>
        </div>
      </div>
    </div>
  );
};
